import { useMemo, useState, useEffect, useCallback } from "react";
import { Modal } from "components/ui-kit/Modal/Modal";
import cn from "classnames";

import { Input } from "components/ui-kit/Input";
import { helperFunctions } from "utilities/helper-functions";
import { Dropdown } from "components/ui-kit/Dropdown";
import MarketDataPageModal from "components/requests/modals/marketdata-modal/MarketDataPageModal";
import { useDispatch, useSelector } from "react-redux";
import { tradeGroupThunks } from "action-thunks/tradeGroup-thunks";

import styles from "./styles.module.scss";
import "./global.scss";

const OnboardingTradingPackageModal = ({ item, closeModal, allAccounts }) => {
  const { tradingPlatforms, marketdataProviders } = useSelector(
    (state) => state.marketdata
  );
  const { TGAccountsList, generatedTraderID, TGsList } = useSelector(
    (state) => state.tradeGroup
  );

  const agency = TGsList?.[0];

  const { isLoading } = useSelector((state) => state.utils);

  const dispatch = useDispatch();

  const [activeTP, setActiveTP] = useState(null);
  const [customTraderID, setCustomTraderID] = useState(generatedTraderID);
  const [pickedCustomIdVariant, setPickedCustomIdVariant] = useState("custom");
  const [onboardingLocalState, setOnboardingLocalState] = useState({
    day_buying_power: null,
    max_loss: null,
    auto_liquidate: null,
    max_position_exposure: null,
  });
  const [md, setMD] = useState(null);
  const [errors, setErrors] = useState({});

  const { reference, personal, _id } = item.item;

  useEffect(() => {
    if (generatedTraderID) {
      setCustomTraderID(generatedTraderID);
    }
  }, [generatedTraderID]);

  const filteredTPs = useMemo(() => {
    return tradingPlatforms
      ?.filter((tp) => tp.active)
      .sort((a, b) => a.order - b.order);
  }, [tradingPlatforms]);

  const activeTradingPlatform = useMemo(() => {
    if (!activeTP) return null;
    return filteredTPs?.find((tp) => tp.reference === activeTP) || null;
  }, [activeTP, filteredTPs]);

  const isDasPlatform = activeTradingPlatform?.software === "DAS";
  const lossFieldName = isDasPlatform ? "auto_liquidate" : "max_loss";
  const lossFieldLabel = isDasPlatform ? "Auto Liquidate" : "Max Loss";
  const activeLossValue = onboardingLocalState[lossFieldName];

  const handleFormSubmit = () => {
    const foundTP = activeTradingPlatform;

    // Handle market data differently for simulator vs regular platforms
    let marketData = [];
    if (isSimulatorPlatform) {
      // For simulator platform, no market data selection needed
      marketData = [];
    } else if (md && md.length > 0) {
      // For regular platforms, process selected market data
      marketData = md
        .map((m) => {
          const foundMD = marketdataProviders.find((mdp) => mdp.name === m);
          if (foundMD) return { group: foundMD.group, market: foundMD.name, reference: foundMD.reference };
          return null;
        })
        .filter((i) => i);
    }

    const rules = {
      initialBuyingPower: onboardingLocalState.day_buying_power,
      max_position_exposure: onboardingLocalState.max_position_exposure,
      performanceBond: onboardingLocalState.performance_bond,
    };
    if (isDasPlatform) {
      rules.auto_liquidate = activeLossValue;
    } else {
      rules.maximumDailyLoss = activeLossValue;
    }

    const body = {
      clientID: _id,
      isGroupMemberFlow: true,
      software: foundTP?.software,
      softwareType: foundTP?.softwareType,
      agreement: {
        rules,
        marketData,
      },
    };
    if (pickedCustomIdVariant === "custom" && !reference?.network) {
      body.reference = { client: customTraderID.trim() };
    }

    dispatch(tradeGroupThunks.createTradingPackageTC(body));
    closeModal();
  };

  const seIsButtonDisabled = () => {
    const hasErrors = Object.values(errors).some((error) => error !== null);

    // For simulator platform, don't require market data selection
    const marketDataRequired = !isSimulatorPlatform ? !!md?.length : true;

    const hasRequiredValues =
      (
          onboardingLocalState.day_buying_power !== null &&
          activeLossValue !== null &&
          onboardingLocalState.max_position_exposure !== null &&
          marketDataRequired &&
          activeTP !== null
      ) || (
          reference?.network
            && onboardingLocalState.performance_bond !== null
            && onboardingLocalState.performance_bond > 0
        );

    let customPrefixValid = true;

    const isCustomIdValid =
      ((pickedCustomIdVariant !== "custom" || customTraderID.trim().length >= 4) && customPrefixValid) || reference?.network;

    return hasErrors || !hasRequiredValues || !isCustomIdValid;
  };

  const handleInputBlur = (e) => {
    const { name, value: rawValue } = e.target;
    const value = rawValue.trim();

    let validatedValue = helperFunctions.validateRiskSettingValue(value, {
      name,
    }, !reference?.network);
    let error = null;

    // Additional error handling based on value
    if (helperFunctions.isRegularCategory({ name }) && value > 1e8) {
      error = "Maximum allowed value: 100,000,000";
    } else if (!helperFunctions.isRegularCategory({ name }) && value > 1e7) {
      error = "Maximum allowed value: 10,000,000";
    }

    if (error) {
      validatedValue = null;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]:
        error || (typeof validatedValue === "string" ? validatedValue : null),
    }));

    setOnboardingLocalState((prevState) => ({
      ...prevState,
      [name]:
        validatedValue !== null && typeof validatedValue !== "string"
          ? validatedValue
          : value,
    }));
  };

  const onRSChangeHandler = (e) => {
    const { name, value } = e.target;
    setOnboardingLocalState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // Check if current account has a simulator account with same email (memoized)
  const hasSimulatorAccount = useMemo(() => {
    if (
      !allAccounts ||
      !item.item?.personal?.email ||
      item.item?.softwareType === "Sim"
    ) {
      return false;
    }
    return allAccounts.some(
      (account) =>
        account.personal?.email === item.item?.personal?.email &&
        account.softwareType === "Sim" &&
        account._id !== item.item?._id
    );
  }, [
    allAccounts,
    item.item?.personal?.email,
    item.item?.softwareType,
    item.item?._id,
  ]);

  const formattedTradingPlatformList = useMemo(() => {
    if (filteredTPs) {
      return filteredTPs
        .filter((i) => {
          // Always filter out das_web_extra
          if (i.reference === "das_web_extra") return false;

          // If current account is simulator, filter out simulator option
          if (
            item.item?.softwareType === "Sim" &&
            i.reference === "sterling_sim"
          ) {
            return false;
          }

          // If current account is live and has simulator account, filter out simulator option
          if (
            item.item?.softwareType !== "Sim" &&
            i.reference === "sterling_sim" &&
            hasSimulatorAccount
          ) {
            return false;
          }

          return true;
        })
        .map((item) => ({
          value: item.reference,
          label: item.name,
        }));
    }
  }, [filteredTPs, item.item?.softwareType, hasSimulatorAccount]);

  const copyLocalState = useCallback((MDLocalState) => {
    if (!MDLocalState) return;

    const nextMarkets = Array.isArray(MDLocalState)
      ? MDLocalState
      : MDLocalState.markets;

    if (!Array.isArray(nextMarkets)) return;

    setMD((prevMarkets) => {
      if (!Array.isArray(prevMarkets)) return nextMarkets;
      if (
        prevMarkets.length === nextMarkets.length &&
        prevMarkets.every((market, idx) => market === nextMarkets[idx])
      ) {
        return prevMarkets;
      }
      return nextMarkets;
    });
  }, []);

  const TPChangeHandler = (value) => {
    setMD(null);
    setActiveTP(value);
    const findTP = filteredTPs.find((tp) => tp.reference === value);
    dispatch(
      tradeGroupThunks.getGeneratedTraderIDTC({
        software: findTP.software,
        softwareType: findTP.softwareType,
      })
    );
  };

  const isSimulatorPlatform = useMemo(() => {
    if (!activeTP) return false;
    return activeTradingPlatform?.reference === "sterling_sim";
  }, [activeTP, activeTradingPlatform]);

  const renderMDPageModal = useMemo(() => {
    if (activeTP) {
      // Hide market data block for simulator platform
      if (isSimulatorPlatform) {
        return (
          <div className={styles.simulatorMarketData}>
            <p className="greyed-text">
              Market data is included with simulator platform at no additional
              cost.
            </p>
            <div className={styles.costDisplay}>
              <strong>Market data monthly cost: $0.00</strong>
            </div>
          </div>
        );
      }

      return (
        <MarketDataPageModal
          onboardingClient
          copyLocalState={copyLocalState}
          activeTP={activeTP}
          defaultTraderIds={[reference.onboardingID]}
          TGAccountsList={TGAccountsList}
          closeModal={closeModal}
        />
      );
    } else {
      return (
        <p className="greyed-text">
          Please specify a trading platform to select market data
        </p>
      );
    }
  }, [
    activeTP,
    reference,
    TGAccountsList,
    closeModal,
    isSimulatorPlatform,
    copyLocalState,
  ]);

  const renderTradingIDBlock = () => {
    if (activeTP || reference?.network) {
      return (
        <div className="d-flex flex-col gap-10 align-items-top modal-row">
          <Input
            labelClassName="cabinet-input"
            variant="zimtra-primary"
            maxLength={40}
            minLength={4}
            disabled={
              isSimulatorPlatform ||
              pickedCustomIdVariant !== "custom" ||
              isLoading
            }
            placeholder="Enter custom ID"
            id="customTraderID"
            name="customTraderID"
            onChange={(e) => {
                setCustomTraderID(e.currentTarget.value);
            }}
            onBlur={(e) => {
                const value = e.currentTarget.value.trim();
                setCustomTraderID(value);

                if (value.length === 0) {
                    setPickedCustomIdVariant('generated');
                } else {
                    setPickedCustomIdVariant('custom');
                }
            }}
            value={customTraderID}
            alertMessage={errors.custom}
          />
        </div>
      );
    } else {
      return (
        <p className="greyed-text">
          Please specify a trading platform to select trader ID
        </p>
      );
    }
  };

  const renderForm = () => {
      if (reference?.network) {
          return (
              <form>
                  <div className="d-flex flex-col gap-20 pb-2">
                      <div className={styles.riskItem}>
                          <span>Performance bond</span>
                          <Input
                              labelClassName="cabinet-input"
                              variant="zimtra-primary"
                              placeholder="Enter a value"
                              id="OnboardingPM_PB"
                              name="performance_bond"
                              onChange={(e) =>
                                  onRSChangeHandler(e, { name: "performance_bond" })
                              }
                              onBlur={handleInputBlur}
                              value={onboardingLocalState.performance_bond ?? ""}
                              alertMessage={errors.performance_bond}
                          />
                      </div>
                  </div>
              </form>
          );
      } else {
          return (<form>
              <h4 className={styles.title}>risk settings</h4>
              <div className="d-flex flex-col gap-20">
                  <div className={styles.riskItem}>
                      <span>Day Buying Power</span>
                      <Input
                          labelClassName="cabinet-input"
                          variant="zimtra-primary"
                          placeholder="Enter a value"
                          id="OnboardingPM_DBP"
                          name="day_buying_power"
                          onChange={(e) =>
                              onRSChangeHandler(e, { name: "day_buying_power" })
                          }
                          onBlur={handleInputBlur}
                          value={onboardingLocalState.day_buying_power ?? ""}
                          alertMessage={errors.day_buying_power}
                      />
                  </div>
                  <div className={styles.riskItem}>
                      <span>{lossFieldLabel}</span>
                      <Input
                          labelClassName="cabinet-input"
                          variant="zimtra-primary"
                          placeholder="Enter a value"
                          id="OnboardingPM_ML"
                          name={lossFieldName}
                          onChange={(e) =>
                              onRSChangeHandler(e, { name: lossFieldName })
                          }
                          onBlur={handleInputBlur}
                          value={activeLossValue ?? ""}
                          alertMessage={errors[lossFieldName]}
                      />
                  </div>
                  <div className={styles.riskItem}>
                      <span>Max Position Exposure</span>
                      <Input
                          labelClassName="cabinet-input"
                          variant="zimtra-primary"
                          placeholder="Enter a value"
                          id="OnboardingPM_MPE"
                          name="max_position_exposure"
                          onChange={(e) =>
                              onRSChangeHandler(e, { name: "max_position_exposure" })
                          }
                          onBlur={handleInputBlur}
                          value={onboardingLocalState.max_position_exposure ?? ""}
                          alertMessage={errors.max_position_exposure}
                      />
                  </div>
              </div>
              <hr />
              <h4 className={styles.title}>trading platform</h4>
              <div className={styles.riskItemColumn}>
                  <div className="d-flex justify-content-between align-items-center">
                      <span>Trading Platform</span>
                      <div className="d-flex align-items-top modal-row">
                          <Dropdown
                              options={formattedTradingPlatformList}
                              className="request-dropdown select-risk-settings-dropdown"
                              placeholder="Select a Trading Platform"
                              onChange={(value) => TPChangeHandler(value)}
                          />
                      </div>
                  </div>
                  {isSimulatorPlatform && (
                      <div className={styles.simulatorWarning}>
                          <p className={styles.warningText}>
                              <strong>
                                  Sterling Simulator comes with NYSE Level 1, AMEX/ARCA
                                  Level 1 and NASDAQ Level 1 market data.
                              </strong>
                          </p>
                          <p className={styles.warningText}>
                              <strong>Important Notice:</strong> By accessing or using
                              market data through the simulator platform, you acknowledge
                              and agree to the following:
                          </p>
                          <ul className={styles.warningList}>
                              <li>
                                  If any exchange or data provider determines that the user
                                  qualifies as a Professional ("PRO") user, they will be
                                  subject to Professional market data fees as defined by the
                                  respective exchange.
                              </li>
                              <li>
                                  User may be classified as a Professional user if market
                                  data is used for business, commercial, or institutional
                                  purposes.
                              </li>
                              <li>
                                  User may also be considered a Professional user if they
                                  are registered or qualified with any securities exchange,
                                  regulatory body, or similar organization, or if they
                                  provide investment advice, manage assets, or otherwise act
                                  in a professional investment capacity.
                              </li>
                              <li>
                                  You are responsible for accurately representing your user
                                  status and for any fees that may result from an exchange's
                                  determination.
                              </li>
                          </ul>
                      </div>
                  )}
              </div>
              <hr />
              <h4 className={styles.title}>Trader ID</h4>
              {renderTradingIDBlock()}
              <hr />
              <h4 className={styles.title}>market data</h4>
              {renderMDPageModal}
          </form>);
      }
  };

  return (
    <div className="modal-window">
      <Modal
        className="new-request-modal-PE cabinet-modals"
        overlayClassName="request-overlay"
        styleType="zimtra-primary"
        isOpen={true}
        hideBlur
        hasCloseIcon={false}
        onClickOutside={closeModal}
      >
        <Modal.Header
          className="modal-inner-header"
          onClose={closeModal}
          title={
            <h2 className="zimtra-h2">
              {`${personal.firstName} ${personal.lastName}`} -{" "}
              {item.item?.softwareType === "Sim"
                ? "Upgrade to live account"
                : "Trading package options"}
            </h2>
          }
        />
        <Modal.Body
          className={cn("modal-inner-body", styles["onboarding-modal-body"])}
        >
            { renderForm() }
        </Modal.Body>
        <Modal.Footer className="modal-inner-footer d-flex justify-content-between">
          <button
            disabled={seIsButtonDisabled()}
            onClick={handleFormSubmit}
            className="main-button"
          >
            Confirm
          </button>
          <button className="main-button cancel-button" onClick={closeModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OnboardingTradingPackageModal;
